/**
 * Shared "traffic overview range" state that lives in localStorage so both
 * `GA4Panel` (on the client Overview tab) and `GscPanel` (on the Search
 * Console tab) stay in sync — changing the toggle on either panel updates
 * both, and the choice persists across page reloads and tab switches.
 *
 * Storage key: `rankos.overview.range`.
 * Valid values: `28d`, `30d`, `60d`, `90d`, `ytd`.
 * Default: `30d` (better than legacy `28d` for a client-facing view).
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "rankos.overview.range";
const DEFAULT_RANGE = "30d";
const VALID = new Set(["28d", "30d", "60d", "90d", "ytd"]);

/** Read + validate from localStorage. SSR-safe. */
function readStored() {
  if (typeof window === "undefined") return DEFAULT_RANGE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && VALID.has(raw)) return raw;
  } catch {
    // access denied (private mode, disabled storage) — silently fall through
  }
  return DEFAULT_RANGE;
}

export function useSyncedRange() {
  const [range, setRangeState] = useState(readStored);

  // Cross-tab / cross-component sync — the `storage` event fires on other
  // hook instances when one instance writes. Combined with a manual custom
  // event for same-tab syncing (storage doesn't fire in the writing tab).
  useEffect(() => {
    function onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      const v = e.newValue;
      if (v && VALID.has(v)) setRangeState(v);
    }
    function onLocal(e) {
      const v = e.detail;
      if (v && VALID.has(v)) setRangeState(v);
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener("rankos:overview-range", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("rankos:overview-range", onLocal);
    };
  }, []);

  const setRange = useCallback((next) => {
    if (!VALID.has(next)) return;
    setRangeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage disabled — hook still works in-memory for this tab
    }
    // Same-tab broadcast so the sibling panel on another route/tab picks it
    // up immediately without needing a full page reload.
    window.dispatchEvent(new CustomEvent("rankos:overview-range", { detail: next }));
  }, []);

  return [range, setRange];
}

export const RANGE_OPTIONS = [
  { key: "30d", label: "30d" },
  { key: "60d", label: "60d" },
  { key: "90d", label: "90d" },
  { key: "ytd", label: "YTD" },
];

export const RANGE_HEADING = {
  "28d": "last 28 days",
  "30d": "last 30 days",
  "60d": "last 60 days",
  "90d": "last 90 days",
  ytd: "year to date",
};
